/*
 * Taken from https://github.com/ramkumarshankar/vue-simple-websocket/blob/master/src/WebSocketClient.js
 * with only 1 modification: removed the console.log call in the constructor.
 *
 * This is taken from our existing WebSocket package, "vue-simple-socket".
 * The package works by configuring the socket client as a Vue plugin, which persists
 * throughout the lifetime of the session. However, now that we're using Inertia, we
 * can load multiple chats within the session. As such, the socket client needs to be
 * torn and recreated each time.
 *
 * For now, lets use the same code but have the Vue component manage it as a piece of
 * component state. This way we avoid updating the entire WebSocket implementation as part of
 * the migration to Inertia.
 */
export default class WebSocketClient {
    constructor(url, options) {
        this.instance = null;
        this.url = url;
        this.options = options || this.defaultOptions();
        if (this.options) {
            this.reconnectEnabled = options.reconnectEnabled || false;
            if (this.reconnectEnabled) {
                this.reconnectInterval = options.reconnectInterval;
                // Optional cap on auto-reconnect attempts (0/undefined = unlimited).
                // Prevents an unrecoverable failure (e.g. revoked auth) from looping forever.
                this.maxReconnectAttempts = options.maxReconnectAttempts || 0;
            }
            // Optional subprotocols list or getter function for WebSocket handshake (e.g. bearer auth).
            // Accepts a function so callers can supply a fresh token on each (re)connect.
            this.subprotocols = options.subprotocols || undefined;
            // Optional async hook awaited before each auto-reconnect (e.g. refresh an expired token).
            this.beforeReconnect = options.beforeReconnect || null;
        }
        this.reconnectAttempts = 0;
        // These methods should be defined by components
        this.onOpen = null;
        this.onMessage = null;
        this.onClose = null;
        this.onError = null;
        // Called when auto-reconnect gives up after maxReconnectAttempts.
        this.onReconnectFailed = null;
    }

    defaultOptions() {
        return {
            reconnectEnabled: false,
            reconnectInterval: 0,
        };
    }

    connect() {
        const subs = typeof this.subprotocols === "function" ? this.subprotocols() : this.subprotocols;
        this.instance = subs ? new WebSocket(this.url, subs) : new WebSocket(this.url);

        // Socket event listeners
        // Each event handler also calls the corresponding class method, which can be defined by the component
        this.instance.onopen = () => {
            this.reconnectAttempts = 0;
            if (typeof this.onOpen === "function") {
                this.onOpen();
            }
        };
        this.instance.onmessage = (msg) => {
            if (typeof this.onMessage === "function") {
                this.onMessage(msg);
            }
        };
        this.instance.onclose = (evt) => {
            if (typeof this.onClose === "function") {
                this.onClose(evt);
            }
            if (!evt.wasClean && this.reconnectEnabled) {
                this.reconnect();
            }
        };
        this.instance.onerror = (evt) => {
            if (typeof this.onError === "function") {
                this.onError(evt);
            }
        };
    }

    reconnect() {
        if (this.maxReconnectAttempts && this.reconnectAttempts >= this.maxReconnectAttempts) {
            if (typeof this.onReconnectFailed === "function") {
                this.onReconnectFailed();
            }
            return;
        }
        this.reconnectAttempts++;
        delete this.instance;
        setTimeout(async () => {
            if (typeof this.beforeReconnect === "function") {
                try {
                    await this.beforeReconnect();
                } catch {
                    // Proceed with the reconnect even if the pre-reconnect hook (e.g. token
                    // refresh) fails; connect() will surface any resulting handshake error.
                }
            }
            this.connect();
        }, this.reconnectInterval);
    }

    close() {
        this.instance.close();
    }

    send(message) {
        this.instance.send(message);
    }

    sendObj(data) {
        this.instance.send(JSON.stringify(data));
    }

    removeListeners() {
        this.onOpen = null;
        this.onMessage = null;
        this.onClose = null;
        this.onError = null;
    }
}
